import { UserCircleIcon, XIcon } from "@heroicons/react/outline";
import { useContext, useState } from "react";
import { patchData } from "../backend";
import { useModal, WorkflowContext } from "../contexts";
import { BLOCK_ICON_MAP, ORDINAL_MAP } from "../utils";
import { useTranslation } from "react-i18next";
import WorkflowOverviewBlockModal from "./WorkflowOverviewBlockModal";
import useUser from "../hooks/useUser";
import Avatar from "./Avatar";
import SmallSelectField from "./SmallSelectField";
import PayWallModalTeams from "./PayWallModalTeams";
import SmallTextInputField from "./SmallTextInputField";
import WorkflowModalShare from "./WorkflowModalShare";
import SettingsTeamModalInvite from "./SettingsTeamModalInvite";

interface Props {
  open: boolean;
  setOpen: (open: boolean) => void;
}

function WorkflowOverviewBlockReviewModal(props: Props) {
  const [isSaving, setIsSaving] = useState(false);

  const { user } = useUser();
  const { openModal, modalIsOpen } = useModal();

  const { t } = useTranslation();
  const { hasPermission } = useUser();
  const { workflow, mutateWorkflow } = useContext(WorkflowContext);

  const documents = workflow.documents;

  const [review, setReview] = useState(workflow!.review);
  const [approvalUserIds, setApprovalUserIds] = useState<string[]>(review?.approvalUserIds ?? []);

  const [inviteEmail, setInviteEmail] = useState("");

  function handleCheckOnChange(value: boolean, userId: string) {
    if (!workflow.team)
      return openModal(<WorkflowModalShare workflow={workflow} />);

    if (value)
      setApprovalUserIds(ids => [...ids, userId])
    else
      setApprovalUserIds(ids => ids.filter(id => id !== userId))
  }

  function handleChangePosition(value: string, userId: string) {
    const index = Number(value);

    const newIds = approvalUserIds.filter(id => id !== userId);
    newIds.splice(index, 0, userId);

    setApprovalUserIds([...newIds]);
  }

  async function handleSave() {
    setIsSaving(true);

    await patchData(`/workflows/reviews/${workflow.review!.id}/`, { ...review, approvalUserIds })
      .then(() => mutateWorkflow())
      .catch(console.error);

    setIsSaving(false);
    props.setOpen(false);
  }

  async function handleInvite() {
    if (!user?.activeTeam)
      return openModal(<PayWallModalTeams />);

    openModal(<SettingsTeamModalInvite initialEmail={inviteEmail} />);
    setInviteEmail("");
  }

  const emailIsValid = /[a-zA-Z0-9._+-]+@[a-zA-Z0-9-]+\.[a-zA-Z0-9.-]{2,}/.test(inviteEmail);

  const isReviewOnly = !workflow.team;
  const hasApprovalUsers = approvalUserIds.length > 0 || review?.approvalsRequireRunner;

  const sortedApprovalUsers = user?.activeTeam?.members.sort((a, b) => {
    if (a.id === user?.id) return -1;
    if (b.id === user?.id) return 1;

    if (!review?.approvalsInSequence) return 0;

    const aIndex = approvalUserIds.indexOf(a.id);
    const bIndex = approvalUserIds.indexOf(b.id);

    if (aIndex === -1 && bIndex === -1) return 0;
    if (aIndex === -1) return 1;
    if (bIndex === -1) return -1;

    return aIndex - bIndex;
  });

  return (
    <WorkflowOverviewBlockModal open={props.open && !modalIsOpen} setOpen={(value) => props.setOpen(modalIsOpen ? !value : value)}>
      <div className="relative flex flex-col gap-6 w-[620px]">
        <XIcon className="absolute w-6 h-6 -top-4 -right-4 cursor-pointer text-gray-400 hover:text-gray-600" type="button" onClick={() => props.setOpen(false)} />

        <div className="flex items-center gap-2">
          <img src={BLOCK_ICON_MAP["REVIEW"]} alt="Review icon" className="h-10 w-10" />
          <div className="flex flex-col">
            <div className="font-gilroy font-semibold">
              {workflow.team ? t("Request Approvals") : t("Review Documents")}
            </div>
            <div className="text-gray-600 text-sm">{t("Review documents yourself or allow selected team members to approve them")}</div>
          </div>
        </div>

        <div className="text-gray-600 text-sm font-semibold -mb-5">
          <div>
            {t("Request Approvals from Team Members")}
          </div>
        </div>
        <div className="flex flex-col max-h-56 overflow-y-auto gap-2 py-2 border-b border-t">
          {(sortedApprovalUsers ?? [workflow.owner!]).map((teamUser) =>
            <div key={teamUser.id} className="flex items-center gap-2 text-sm">
              <input type="checkbox" className="h-4 w-4" checked={(!isReviewOnly && approvalUserIds.includes(teamUser.id)) || (isReviewOnly && teamUser.id === user?.id)} onChange={(e) => handleCheckOnChange(e.target.checked, teamUser.id)} disabled={(isReviewOnly && !(user?.activeTeam && teamUser.id !== user?.id)) || (!approvalUserIds.includes(teamUser.id) && teamUser.teamRole === "VIEWER")} />
              <Avatar url={teamUser.avatar} className="h-8 w-8 flex-shrink-0" />
              <div className="flex flex-col text-xs">
                <p className="font-gilroy font-bold whitespace-nowrap overflow-hidden text-ellipsis text-gray-600">
                  {teamUser.fullName}
                  {teamUser.teamRole === "VIEWER" &&
                    <span className="text-gray-400"> ({t("Viewer")})</span>
                  }
                </p>
                <p className="text-gray-400 whitespace-nowrap overflow-hidden text-ellipsis">{teamUser.email}</p>
              </div>
              {((review?.approvalsRequireAll && review?.approvalsInSequence) && approvalUserIds.includes(teamUser.id)) &&
                <SmallSelectField className="ml-auto text-xs text-gray-400 w-16" value={String(approvalUserIds.indexOf(teamUser.id))} onChange={(value) => handleChangePosition(value, teamUser.id)}>
                  <>
                    {Array.from({ length: approvalUserIds.length }).fill(0).map((_, index) =>
                      <option key={String(index)} value={String(index)}>{t(ORDINAL_MAP[index + 1])}</option>
                    )}
                  </>
                </SmallSelectField>
              }
            </div>
          )}
          {workflow.team &&
            <div className="flex items-center gap-2 text-sm">
              <input type="checkbox" className="h-4 w-4" checked={review?.approvalsRequireRunner} onChange={(e) => setReview(prev => ({ ...prev!, approvalsRequireRunner: e.target.checked }))} />
              <UserCircleIcon className="h-8 w-8 flex-shrink-0 text-gray-400" />
              <div className="flex flex-col text-xs">
                <p className="font-gilroy font-bold whitespace-nowrap overflow-hidden text-ellipsis text-gray-600">
                  {t("Workflow Runner")}
                  <span className="text-blue"> ({t("Dynamic")})</span>
                </p>
                <p className="text-gray-400 whitespace-nowrap overflow-hidden text-ellipsis">
                  {t("The person who initiated the automation")}
                </p>
              </div>
            </div>
          }
          <div className="flex items-end gap-2 text-sm">
            <SmallTextInputField className="w-full" value={inviteEmail} label="Email" placeholder="Add Approver" onChange={(value) => setInviteEmail(value)} />
            <button className="btn btn-blue py-[5px]" onClick={handleInvite} disabled={!emailIsValid}>Invite</button>
          </div>
        </div>

        <div className="flex flex-col gap-2 text-sm">
          <div className="text-xs text-gray-600 font-semibold -mt-2 mb-2">
            {t("If no specific team members are selected, any team member can approve documents")}
          </div>

          <div className="flex items-center gap-2 font-semibold text-gray-600">
            <input type="checkbox" className="h-4 w-4" checked={hasApprovalUsers && review?.approvalsRequireAll && (approvalUserIds.length >= 2 || (approvalUserIds.length >= 1 && review.approvalsRequireRunner))} onChange={(e) => setReview(prev => ({ ...prev!, approvalsRequireAll: !!e.target?.checked }))} disabled={review?.approvalsRequireRunner ? approvalUserIds.length < 1 : approvalUserIds.length < 2} />
            {t("Require all selected team members to approve documents")}
          </div>
          <div className="flex items-center gap-2 font-semibold text-gray-600 text-sm">
            <input type="checkbox" className="h-4 w-4" checked={hasApprovalUsers && review?.approvalsNotify} onChange={(e) => setReview(prev => ({ ...prev!, approvalsNotify: !!e.target?.checked }))} disabled={!hasApprovalUsers} />
            {workflow.team
              ? t("Notify approvers when documents are ready to review")
              : t("Notify me when documents are ready to review")
            }
          </div>
          {review?.approvalsNotify &&
            <textarea className="w-full h-24 p-2 border border-gray-300 rounded resize-none" value={review.approvalsMessage} placeholder={t("Message")} onChange={(e) => setReview(prev => ({ ...prev!, approvalsMessage: e.target.value }))} disabled={!hasApprovalUsers} />
          }
        </div>

        <div className="flex justify-between items-center">
          <div className="text-xs text-gray-600 mr-8">
            {t("Once documents have been generated they will not continue to be processed until they have been approved.")}
          </div>
          {hasPermission(workflow.team, "workflows.edit") && documents.length > 0 &&
            <button className="btn btn-blue text-sm w-48" onClick={handleSave} type="button" disabled={isSaving}>{isSaving ? t("Saving...") : t("Save")}</button>
          }
        </div>

      </div>
    </WorkflowOverviewBlockModal>
  );
}

export default WorkflowOverviewBlockReviewModal;
